import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  CheckCircle2,
  XCircle,
  MoonStar,
  Sun,
  BedDouble,
  Loader2,
  User,
  Users,
} from "lucide-react";

export default function CheckinPage() {
  const { reference } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/bookings/lookup/${reference}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Prenotazione non trovata");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reference]);

  return (
    <div
      data-testid="checkin-page"
      className="min-h-screen bg-primary flex items-center justify-center px-6 py-10"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl">
        {loading && (
          <div className="p-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          </div>
        )}

        {!loading && error && (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <XCircle className="w-7 h-7 text-destructive" />
            </div>
            <div className="font-display text-3xl text-primary mb-2">Non trovata</div>
            <p className="text-muted-foreground">
              La prenotazione <strong>{reference}</strong> non esiste o è stata annullata.
            </p>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="bg-primary text-primary-foreground p-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.28em] opacity-80">
                    Prenotazione confermata
                  </div>
                  <div className="font-display text-3xl mt-1">
                    {data.reference}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 space-y-5">
              <div className="flex items-start gap-3">
                <BedDouble className="w-5 h-5 mt-1 text-primary shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Camera
                  </div>
                  <div className="font-display text-2xl text-primary">
                    {data.room_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {data.property_name}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                {data.booking_type === "dayuse" ? (
                  <Sun className="w-5 h-5 mt-1 text-secondary shrink-0" />
                ) : (
                  <MoonStar className="w-5 h-5 mt-1 text-primary shrink-0" />
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {data.booking_type === "dayuse" ? "DayUse" : "Pernottamento"}
                  </div>
                  <div className="text-primary text-lg">
                    {data.booking_type === "dayuse" ? (
                      <>
                        {format(new Date(data.check_in), "EEEE d MMMM yyyy", {
                          locale: it,
                        })}
                        <div className="text-base mt-1">
                          <strong>{data.slot_start}</strong> — {data.slot_end}
                        </div>
                      </>
                    ) : (
                      <>
                        {format(new Date(data.check_in), "d MMM", { locale: it })}{" "}
                        →{" "}
                        {format(new Date(data.check_out), "d MMM yyyy", {
                          locale: it,
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="w-5 h-5 mt-1 text-primary shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Ospite
                  </div>
                  <div className="text-primary text-lg">
                    {data.guest_first_name} {data.guest_last_name}
                  </div>
                </div>
              </div>

              {data.guests > 1 && (
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 mt-1 text-primary shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      Ospiti
                    </div>
                    <div className="text-primary text-lg">{data.guests}</div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-border flex items-baseline justify-between">
                <div className="text-sm text-muted-foreground">Totale</div>
                <div className="font-display text-3xl text-secondary">
                  €{data.price}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
